import { useUiText } from "../../locale/UiLocale";
import type { KeyboardEvent } from "react";
import { Popover } from "../Popover/Popover";
import { optionId, type SelectOption } from "./selectOption";
import { SelectOptionItem } from "./SelectOptionItem";
import { SelectSearch } from "./SelectSearch";
import type { SelectList } from "./useSelectList";

interface SelectListboxProps {
  list: SelectList;
  value: string;
  /** Shows the filter box (with this placeholder) above the options. */
  search?: { placeholder: string; onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void };
  label?: string;
  labelledBy?: string;
  onCommit: (option: SelectOption) => void;
}

/** The open list, in a popover as wide as the trigger at least. */
export function SelectListbox({ list, value, search, label, labelledBy, onCommit }: SelectListboxProps): React.JSX.Element {
  const t = useUiText();
  const { listId, visible, active } = list;
  return (
    <Popover
      anchorRef={list.triggerRef}
      open={list.open}
      onClose={list.dismiss}
      align="start"
      role="presentation"
      minWidth={list.triggerRef.current?.offsetWidth}
      className="nb-select-pop"
    >
      {search ? (
        <SelectSearch
          inputRef={list.searchRef}
          listId={listId}
          activeDescendant={active >= 0 && visible[active] ? optionId(listId, active) : undefined}
          placeholder={search.placeholder}
          query={list.query}
          onQueryChange={list.changeQuery}
          onKeyDown={search.onKeyDown}
        />
      ) : null}
      <ul ref={list.listRef} id={listId} role="listbox" aria-label={label} aria-labelledby={labelledBy} className="nb-select-list-box" tabIndex={-1}>
        {visible.map((option, index) => (
          <SelectOptionItem
            key={option.value}
            id={optionId(listId, index)}
            option={option}
            index={index}
            selected={option.value === value}
            active={index === active}
            onActivate={list.setActive}
            onCommit={onCommit}
          />
        ))}
        {visible.length === 0 ? (
          <li role="presentation" className="nb-select-empty">
            {t("No results")}
          </li>
        ) : null}
      </ul>
    </Popover>
  );
}
